import { useEffect, useState } from 'react'
import {
  getStudioChurnRisk,
  getStudioRetentionCohorts,
  getStudioVisitFrequency,
  type StudioChurnRiskRow,
  type StudioRetentionCohortRow,
  type StudioVisitFrequencyRow,
} from '@/services/clientLifecycleService'

interface AsyncState<T> {
  data: T
  loading: boolean
  error: string | null
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

export function useStudioRetention(params: {
  studioInternalId: number | null
  cohortFrom: string | null
  cohortTo: string | null
}) {
  const [state, setState] = useState<AsyncState<StudioRetentionCohortRow[]>>({
    data: [],
    loading: false,
    error: null,
  })

  useEffect(() => {
    if (!params.studioInternalId || !params.cohortFrom || !params.cohortTo) {
      setState({ data: [], loading: false, error: null })
      return
    }

    let cancelled = false
    setState((prev) => ({ ...prev, loading: true, error: null }))

    getStudioRetentionCohorts({
      studioInternalId: params.studioInternalId,
      cohortFrom: params.cohortFrom,
      cohortTo: params.cohortTo,
    })
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null })
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error: getErrorMessage(error) })
      })

    return () => {
      cancelled = true
    }
  }, [params.studioInternalId, params.cohortFrom, params.cohortTo])

  return state
}

export function useChurnRisk(params: {
  studioInternalId: number | null
  idleDaysMin?: number
  idleDaysMax?: number
}) {
  const [state, setState] = useState<AsyncState<StudioChurnRiskRow[]>>({
    data: [],
    loading: false,
    error: null,
  })

  useEffect(() => {
    if (!params.studioInternalId) {
      setState({ data: [], loading: false, error: null })
      return
    }

    let cancelled = false
    setState((prev) => ({ ...prev, loading: true, error: null }))

    getStudioChurnRisk({
      studioInternalId: params.studioInternalId,
      idleDaysMin: params.idleDaysMin,
      idleDaysMax: params.idleDaysMax,
    })
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null })
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error: getErrorMessage(error) })
      })

    return () => {
      cancelled = true
    }
  }, [params.studioInternalId, params.idleDaysMin, params.idleDaysMax])

  return state
}

export function useVisitFrequency(params: {
  studioInternalId: number | null
  dateFrom: string | null
  dateTo: string | null
}) {
  const [state, setState] = useState<AsyncState<StudioVisitFrequencyRow[]>>({
    data: [],
    loading: false,
    error: null,
  })

  useEffect(() => {
    if (!params.studioInternalId || !params.dateFrom || !params.dateTo) {
      setState({ data: [], loading: false, error: null })
      return
    }

    let cancelled = false
    setState((prev) => ({ ...prev, loading: true, error: null }))

    getStudioVisitFrequency({
      studioInternalId: params.studioInternalId,
      dateFrom: params.dateFrom,
      dateTo: params.dateTo,
    })
      .then((data) => {
        if (!cancelled) setState({ data, loading: false, error: null })
      })
      .catch((error) => {
        if (!cancelled) setState({ data: [], loading: false, error: getErrorMessage(error) })
      })

    return () => {
      cancelled = true
    }
  }, [params.studioInternalId, params.dateFrom, params.dateTo])

  return state
}
